import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import db from '../../firebase/firebase';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import {
    Container, Card, CardBody, Form, FormGroup, Label, Input, Button, Row, Col
} from 'reactstrap';

export default function AddIllustration() {
    const navigate = useNavigate();
    const location = useLocation();
    const storage = getStorage();

    // If editing, location.state.illustration is passed
    const existing = location.state?.illustration || null;
    const isEdit = !!existing;

    const [title, setTitle] = useState(existing?.title || '');
    const [caption, setCaption] = useState(existing?.caption || '');
    const [order, setOrder] = useState(existing?.order !== undefined ? String(existing.order) : '');

    // Existing image from Firestore
    const [existingImageUrl, setExistingImageUrl] = useState(existing?.imageUrl || '');
    const [existingImagePath, setExistingImagePath] = useState(existing?.imagePath || '');
    const [existingImageName, setExistingImageName] = useState(existing?.imageName || '');

    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [loading, setLoading] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Please upload an image file.');
            e.target.value = null;
            return;
        }

        const maxMB = 15;
        if (file.size > maxMB * 1024 * 1024) {
            alert(`File too large. Maximum allowed size is ${maxMB} MB.`);
            e.target.value = null;
            return;
        }

        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setUploadProgress(0);
    };

    const clearFile = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setUploadProgress(0);
        const input = document.getElementById('illus-upload');
        if (input) input.value = '';
    };

    const uploadFile = (file) => {
        return new Promise((resolve, reject) => {
            const timestamp = Date.now();
            const filePath = `illustrations/${timestamp}_${file.name}`;
            const ref = storageRef(storage, filePath);
            const uploadTask = uploadBytesResumable(ref, file);

            uploadTask.on(
                'state_changed',
                (snap) => {
                    const prog = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
                    setUploadProgress(prog);
                },
                reject,
                async () => {
                    try {
                        const url = await getDownloadURL(uploadTask.snapshot.ref);
                        resolve({ url, path: filePath, name: file.name });
                    } catch (e) {
                        reject(e);
                    }
                }
            );
        });
    };

    const handleSave = async () => {
        if (!title.trim()) {
            alert('Title is required.');
            return;
        }
        if (!isEdit && !selectedFile) {
            alert('Please select an image to upload.');
            return;
        }

        setLoading(true);
        try {
            let fileData = null;

            if (selectedFile) {
                fileData = await uploadFile(selectedFile);

                // If editing and replacing image, delete old one from storage
                if (isEdit && existingImagePath) {
                    try {
                        const oldRef = storageRef(storage, existingImagePath);
                        await deleteObject(oldRef).catch(err => {
                            if (err.code !== 'storage/object-not-found') console.warn(err);
                        });
                    } catch (e) {
                        console.warn('Could not delete old image:', e);
                    }
                }
            }

            const payload = {
                title: title.trim(),
                caption: caption.trim(),
                order: order !== '' ? parseInt(order, 10) : 9999,
                imageUrl: fileData ? fileData.url : existingImageUrl,
                imagePath: fileData ? fileData.path : existingImagePath,
                imageName: fileData ? fileData.name : existingImageName,
            };

            if (isEdit) {
                await setDoc(doc(db, 'illustrations', existing.id), payload, { merge: true });
            } else {
                payload.createdAt = serverTimestamp();
                await addDoc(collection(db, 'illustrations'), payload);
            }

            navigate('/admin/illustrations');
        } catch (err) {
            console.error('Error saving illustration:', err);
            alert('Error saving illustration. Please try again.');
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    return (
        <Container fluid className="py-4 admin-illustration-editor">
            <Button color="link" onClick={() => navigate('/admin/illustrations')}>
                &larr; Back
            </Button>

            <h3 className="mb-4">{isEdit ? 'Edit Illustration' : 'Add Illustration'}</h3>

            <Card className="mb-4 admin-illustration-editor-card">
                <CardBody>
                    <Form>
                        <FormGroup>
                            <Label>Title <span style={{ color: 'red' }}>*</span></Label>
                            <Input
                                placeholder="e.g. Femur Transverse Fracture"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Caption / Description</Label>
                            <Input
                                type="textarea"
                                rows={3}
                                placeholder="Optional description shown below the image…"
                                value={caption}
                                onChange={e => setCaption(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Display Order (optional, lower = earlier)</Label>
                            <Input
                                type="number"
                                placeholder="e.g. 1"
                                value={order}
                                onChange={e => setOrder(e.target.value)}
                                style={{ maxWidth: 160 }}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>
                                Image (landscape format recommended){!isEdit && <span style={{ color: 'red' }}> *</span>}
                            </Label>

                            {/* Show existing image when editing */}
                            {isEdit && existingImageUrl && !selectedFile && (
                                <div className="admin-illustration-current">
                                    <p style={{ fontSize: 13, color: '#555', margin: '0 0 6px' }}>Current image:</p>
                                    <img
                                        src={existingImageUrl}
                                        alt="Current"
                                        style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 8, border: '1px solid #ddd' }}
                                    />
                                    <div style={{ marginTop: 6, fontSize: 13, color: '#777' }}>{existingImageName}</div>
                                </div>
                            )}

                            <Input
                                id="illus-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                            {isEdit && <small style={{ color: '#888' }}>Leave empty to keep the current image.</small>}

                            {previewUrl && (
                                <div className="admin-illustration-preview">
                                    <img
                                        src={previewUrl}
                                        alt="Preview"
                                        style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 8, border: '1px solid #ddd' }}
                                    />
                                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <span style={{ fontSize: 13, color: '#555' }}>{selectedFile.name}</span>
                                        <Button color="link" size="sm" onClick={clearFile} style={{ color: 'red', padding: 0 }}>
                                            Remove
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <div style={{ marginTop: 10 }}>
                                    <div style={{ background: '#e9ecef', borderRadius: 6, overflow: 'hidden', height: 8 }}>
                                        <div style={{ width: `${uploadProgress}%`, background: '#02af90', height: '100%', transition: 'width 0.3s' }} />
                                    </div>
                                    <small style={{ color: '#555' }}>Uploading… {uploadProgress}%</small>
                                </div>
                            )}
                        </FormGroup>

                        <Row className="mt-4">
                            <Col className="d-flex gap-2">
                                <Button color="primary" onClick={handleSave} disabled={loading}>
                                    {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Illustration'}
                                </Button>
                                <Button color="secondary" outline onClick={() => navigate('/admin/illustrations')} disabled={loading}>
                                    Cancel
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </CardBody>
            </Card>
        </Container>
    );
}
